// frontend/src/pages/PyrPage.jsx
import { useState, useRef, useEffect } from 'react';
import { useWakeLock } from '../hooks/useWakeLock';
import { TimerAudioManager } from '../components/timer/TimerAudioManager';
import { buildSequence, buildPhases, calcTotalSeconds, formatTime } from './pyr.logic';
import './HIITPage.css';
import './TimerStopwatch.css';
import './PyrPage.css';

const PRESETS = [
  { name: 'Einstieg',       start: 15, step: 15, levels: 3, rest: 15, intensity: 2, char: 'Sanfter Einstieg – kürzere Stufen, viel Erholung' },
  { name: 'Klassisch',      start: 20, step: 20, levels: 4, rest: 20, intensity: 3, char: 'Ausgewogen – bewährtes Pyramiden-Format' },
  { name: 'Kraft-Pyramide', start: 30, step: 15, levels: 4, rest: 15, intensity: 4, char: 'Höhere Last – solide Kraftausdauer-Basis' },
  { name: 'Maximalkraft',   start: 20, step: 20, levels: 5, rest: 10, intensity: 5, char: 'Maximaler Peak – nur für Fortgeschrittene' },
];

const PIP_COLORS = ['', '#3B6D11', '#639922', '#EF9F27', '#f97316', '#E24B4A'];

function IntensityPips({ level }) {
  return (
    <div className="hiit-intensity-pips">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className="hiit-pip"
          style={{ background: i <= level ? PIP_COLORS[level] : 'rgba(255,255,255,0.12)' }}
        />
      ))}
    </div>
  );
}

/**
 * Pyramiden-Balken-Grafik — für Config-Screen (activeStepIdx=null)
 * und Minimap im Running-Screen (activeStepIdx gesetzt).
 *
 * @param {number} maxHeight - maximale Balkenhöhe in px (Default 70 für Config-Container 80px;
 *                              im Minimap 42 für Container 48px wegen overflow:hidden)
 */
export function PyramidBars({ seq, peakIdx, activeStepIdx = null, phase = null, maxHeight = 70 }) {
  const peak = seq[peakIdx];
  return (
    <div className="pyr-bars">
      {seq.map((dur, i) => {
        let cls = 'pyr-bar';
        if (activeStepIdx === null) {
          cls += i === peakIdx ? ' pyr-bar--peak' : i < peakIdx ? ' pyr-bar--up' : ' pyr-bar--down';
        } else {
          if (i < activeStepIdx)       cls += ' pyr-bar--done';
          else if (i === activeStepIdx) cls += phase === 'rest' ? ' pyr-bar--rest-active' : ' pyr-bar--active';
          else                          cls += ' pyr-bar--future';
        }
        const h = Math.max(6, Math.round((dur / peak) * maxHeight));
        return (
          <div key={i} className="pyr-bar-wrap">
            <div className={cls} style={{ height: `${h}px` }} />
            {activeStepIdx === null && <div className="pyr-bar-label">{dur}s</div>}
          </div>
        );
      })}
    </div>
  );
}

export default function PyrPage() {
  // ── Config state ──────────────────────────────────────────────────────────
  const [start,          setStart]          = useState(15);
  const [step,           setStep]           = useState(15);
  const [levels,         setLevels]         = useState(4);
  const [restSecs,       setRestSecs]       = useState(15);
  const [soundsEnabled,  setSoundsEnabled]  = useState(true);
  const [selectedPreset, setSelectedPreset] = useState(null);

  // ── Timer state ───────────────────────────────────────────────────────────
  const [view,     setView]     = useState('config'); // config|countdown|running|paused|done
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [cdNum,    setCdNum]    = useState(3);
  const [runKey,   setRunKey]   = useState(0);

  // ── Refs (stale-closure-frei) ─────────────────────────────────────────────
  const intervalRef      = useRef(null);
  const phaseIdxRef      = useRef(0);
  const timeLeftRef      = useRef(0);
  const phaseEndTimeRef  = useRef(null);
  const phasesRef        = useRef([]);
  const soundsEnabledRef = useRef(soundsEnabled);
  const lastBeepSecRef   = useRef(null);
  const pausedRemMsRef   = useRef(null);
  const animFrameRef     = useRef(null);
  const progressBarRef   = useRef(null);
  // Config-Werte als Refs für Countdown-Closure
  const startRef         = useRef(start);
  const stepRef          = useRef(step);
  const levelsRef        = useRef(levels);
  const restSecsRef      = useRef(restSecs);

  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = new TimerAudioManager();

  useEffect(() => { soundsEnabledRef.current = soundsEnabled; }, [soundsEnabled]);
  useEffect(() => { startRef.current  = start;  }, [start]);
  useEffect(() => { stepRef.current   = step;   }, [step]);
  useEffect(() => { levelsRef.current = levels; }, [levels]);
  useEffect(() => { restSecsRef.current = restSecs; }, [restSecs]);
  useEffect(() => () => audioRef.current?.destroy(), []);

  useWakeLock(view === 'running');

  // ── Derived config (für Config-Screen-Anzeige) ────────────────────────────
  const seq      = buildSequence(start, step, levels);
  const peakIdx  = levels;
  const totalSec = calcTotalSeconds(seq, restSecs);

  // ── Countdown 3-2-1 ───────────────────────────────────────────────────────
  useEffect(() => {
    if (view !== 'countdown') return;
    let count = 3;
    setCdNum(3);
    if (soundsEnabledRef.current) audioRef.current.playCountdownBeep();
    const id = setInterval(() => {
      count--;
      if (count > 0) {
        setCdNum(count);
        if (soundsEnabledRef.current) audioRef.current.playCountdownBeep();
      } else {
        clearInterval(id);
        const runSeq    = buildSequence(startRef.current, stepRef.current, levelsRef.current);
        const phases    = buildPhases(runSeq, restSecsRef.current);
        phasesRef.current       = phases;
        phaseIdxRef.current     = 0;
        timeLeftRef.current     = phases[0].dur;
        phaseEndTimeRef.current = Date.now() + phases[0].dur * 1000;
        lastBeepSecRef.current  = null;
        setPhaseIdx(0);
        setTimeLeft(phases[0].dur);
        setView('running');
        if (soundsEnabledRef.current) audioRef.current.playStartExercise();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [view]);

  // ── Running timer (200ms Wall-Clock-Polling) ──────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (view !== 'running') {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      const remainingMs = phaseEndTimeRef.current - Date.now();
      if (remainingMs <= 0) {
        const phases      = phasesRef.current;
        const curPhase    = phases[phaseIdxRef.current];
        const nextIdx     = phaseIdxRef.current + 1;
        if (curPhase.type === 'work' && soundsEnabledRef.current) {
          audioRef.current.playBuzzer();
        }
        if (nextIdx >= phases.length) {
          clearInterval(intervalRef.current);
          setView('done');
          return;
        }
        const nextPhase             = phases[nextIdx];
        phaseIdxRef.current         = nextIdx;
        timeLeftRef.current         = nextPhase.dur;
        phaseEndTimeRef.current     = Date.now() + nextPhase.dur * 1000;
        lastBeepSecRef.current      = null;
        setPhaseIdx(nextIdx);
        setTimeLeft(nextPhase.dur);
        if (soundsEnabledRef.current) {
          // Rest-Start trägt schon den Buzzer als Endsignal — kein zusätzliches playBreak,
          // sonst überlagert sich der Break-Sound mit dem Buzzer.
          if (nextPhase.type === 'work') audioRef.current.playStartExercise();
        }
      } else {
        const secs = Math.ceil(remainingMs / 1000);
        timeLeftRef.current = secs;
        setTimeLeft(secs);
        const curPhase = phasesRef.current[phaseIdxRef.current];
        if (
          soundsEnabledRef.current &&
          curPhase?.type === 'rest' &&
          secs <= 10 &&
          secs !== lastBeepSecRef.current
        ) {
          lastBeepSecRef.current = secs;
          audioRef.current.playCountdownBeep();
        }
      }
    }, 200);
    return () => clearInterval(intervalRef.current);
  }, [view, runKey]);

  // ── Smooth progress bar via requestAnimationFrame ─────────────────────────
  useEffect(() => {
    if (view !== 'running') {
      if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
      return;
    }
    function rafTick() {
      if (!progressBarRef.current) return;
      const remainingMs = Math.max(0, phaseEndTimeRef.current - Date.now());
      const phaseDur    = phasesRef.current[phaseIdxRef.current]?.dur ?? 1;
      const pct         = Math.min(100, (remainingMs / (phaseDur * 1000)) * 100);
      progressBarRef.current.style.width = `${pct}%`;
      animFrameRef.current = requestAnimationFrame(rafTick);
    }
    animFrameRef.current = requestAnimationFrame(rafTick);
    return () => {
      if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    };
  }, [view, runKey]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleStart() {
    audioRef.current.unlock();
    setView('countdown');
  }

  function handlePause() {
    pausedRemMsRef.current = Math.max(0, phaseEndTimeRef.current - Date.now());
    clearInterval(intervalRef.current);
    setView('paused');
  }

  function handleResume() {
    phaseEndTimeRef.current = Date.now() + (pausedRemMsRef.current ?? timeLeftRef.current * 1000);
    pausedRemMsRef.current  = null;
    lastBeepSecRef.current  = null;
    setView('running');
  }

  function handleReset() {
    clearInterval(intervalRef.current);
    setView('countdown');
  }

  function handleSkip() {
    clearInterval(intervalRef.current);
    const phases   = phasesRef.current;
    const curPhase = phases[phaseIdxRef.current];
    if (curPhase.type === 'work' && soundsEnabledRef.current) audioRef.current.playBuzzer();
    const nextIdx = phaseIdxRef.current + 1;
    if (nextIdx >= phases.length) { setView('done'); return; }
    const nextPhase             = phases[nextIdx];
    phaseIdxRef.current         = nextIdx;
    timeLeftRef.current         = nextPhase.dur;
    phaseEndTimeRef.current     = Date.now() + nextPhase.dur * 1000;
    lastBeepSecRef.current      = null;
    setPhaseIdx(nextIdx);
    setTimeLeft(nextPhase.dur);
    if (soundsEnabledRef.current) {
      if (nextPhase.type === 'work') audioRef.current.playStartExercise();
    }
    if (view === 'running') setRunKey((k) => k + 1);
    else setView('running');
  }

  function handleAbort() {
    clearInterval(intervalRef.current);
    setView('config');
  }

  function handlePresetSelect(i) {
    const p = PRESETS[i];
    setStart(p.start);
    setStep(p.step);
    setLevels(p.levels);
    setRestSecs(p.rest);
    setSelectedPreset(i);
  }

  function changeStepper(setter, delta, min, max) {
    setter((v) => Math.max(min, Math.min(max, v + delta)));
    setSelectedPreset(null);
  }

  // ── Derived timer values ──────────────────────────────────────────────────
  const isTimerMode  = view !== 'config';
  const currentPhase = phasesRef.current[phaseIdx] ?? { type: 'work', dur: 0, stepIdx: 0 };
  const isWork       = currentPhase.type === 'work';
  const phaseColor   = isWork ? '#ef4444' : '#60a5fa';
  const timerBg      = (view === 'running' || view === 'paused')
    ? (isWork ? '#1a0d0d' : '#0d1e33')
    : '#1a2332';
  const runSeq       = phasesRef.current.length > 0
    ? buildSequence(startRef.current, stepRef.current, levelsRef.current)
    : seq;
  const runPeakIdx   = levelsRef.current;
  const [timerMins, timerSecs] = formatTime(timeLeft).split(':');
  const isPeak       = isWork && currentPhase.stepIdx === runPeakIdx;

  return (
    <div
      className={isTimerMode ? 'hiit-fullscreen' : undefined}
      style={isTimerMode ? { background: timerBg } : undefined}
      data-theme={isTimerMode ? 'dark' : undefined}
      data-bs-theme={isTimerMode ? 'dark' : undefined}
    >

      {/* ── CONFIG ─────────────────────────────────────────────────────── */}
      {view === 'config' && (
        <>
          <h4 className="mb-4" style={{ color: 'var(--color-text)', fontWeight: 700 }}>
            <i className="bi bi-triangle me-2" />
            Pyramide Quick Start
          </h4>

          <div className="tools-card mb-4">

            <div className="hiit-section-label">Start-Zeit</div>
            <div className="pyr-stepper">
              <button className="tools-btn-ghost pyr-stepper-btn" onClick={() => changeStepper(setStart, -5, 5, 120)}>−</button>
              <span className="pyr-stepper-val">{start}<span className="pyr-stepper-unit"> s</span></span>
              <button className="tools-btn-ghost pyr-stepper-btn" onClick={() => changeStepper(setStart, +5, 5, 120)}>+</button>
            </div>

            <div className="hiit-section-label">Stufensprung</div>
            <div className="pyr-stepper">
              <button className="tools-btn-ghost pyr-stepper-btn" onClick={() => changeStepper(setStep, -5, 5, 60)}>−</button>
              <span className="pyr-stepper-val">{step}<span className="pyr-stepper-unit"> s</span></span>
              <button className="tools-btn-ghost pyr-stepper-btn" onClick={() => changeStepper(setStep, +5, 5, 60)}>+</button>
            </div>

            <div className="hiit-section-label">Anzahl Stufen bis Peak</div>
            <div className="pyr-stepper">
              <button className="tools-btn-ghost pyr-stepper-btn" onClick={() => changeStepper(setLevels, -1, 1, 8)}>−</button>
              <span className="pyr-stepper-val">{levels}</span>
              <button className="tools-btn-ghost pyr-stepper-btn" onClick={() => changeStepper(setLevels, +1, 1, 8)}>+</button>
            </div>

            <div className="hiit-section-label">Pause zwischen Stufen</div>
            <div className="pyr-stepper">
              <button className="tools-btn-ghost pyr-stepper-btn" onClick={() => changeStepper(setRestSecs, -5, 0, 120)}>−</button>
              <span className="pyr-stepper-val">{restSecs}<span className="pyr-stepper-unit"> s</span></span>
              <button className="tools-btn-ghost pyr-stepper-btn" onClick={() => changeStepper(setRestSecs, +5, 0, 120)}>+</button>
            </div>

            <div className="hiit-section-label mt-3">Pyramiden-Vorschau</div>
            <div className="pyr-viz-wrap">
              <PyramidBars seq={seq} peakIdx={peakIdx} />
              <div className="pyr-seq-text">
                Sequenz: {seq.map((v) => v + 's').join(' → ')}
              </div>
            </div>

            <div className="hiit-summary-chips">
              {[
                { label: 'Stufen',      val: seq.length },
                { label: 'Peak',        val: seq[peakIdx] + 's' },
                { label: 'Pause',       val: restSecs + 's' },
                { label: 'Gesamt ca.', val: Math.ceil(totalSec / 60) + ' min' },
              ].map(({ label, val }) => (
                <div key={label} className="hiit-sum-chip">
                  <div className="hiit-sum-chip-label">{label}</div>
                  <div className="hiit-sum-chip-val">{val}</div>
                </div>
              ))}
            </div>

            <div className="form-check mt-3 mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="pyr_sounds"
                checked={soundsEnabled}
                onChange={(e) => setSoundsEnabled(e.target.checked)}
              />
              <label className="form-check-label hiit-sounds-label" htmlFor="pyr_sounds">
                <i className="bi bi-volume-up me-1" />
                Töne aktivieren (Buzzer &amp; Beeps)
              </label>
            </div>

            <button
              className="workout-control-btn-fullwidth"
              onClick={handleStart}
            >
              <i className="bi bi-play-fill" />
              Workout starten
            </button>
          </div>

          <div className="tools-card">
            <div className="hiit-section-label">1-Klick Presets</div>
            <div className="hiit-presets-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {PRESETS.map((p, i) => {
                const pSeq = buildSequence(p.start, p.step, p.levels);
                const dur  = Math.ceil(calcTotalSeconds(pSeq, p.rest) / 60);
                return (
                  <div
                    key={p.name}
                    className={`hiit-preset-card${selectedPreset === i ? ' selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handlePresetSelect(i)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePresetSelect(i)}
                  >
                    <div className="hiit-preset-name">{p.name}</div>
                    <div className="hiit-preset-stats">Start {p.start}s · +{p.step}s · {pSeq.length} Stufen</div>
                    <div className="hiit-preset-char">{p.char}</div>
                    <div className="hiit-preset-footer">
                      <span className="hiit-preset-dur">ca. {dur} min</span>
                      <IntensityPips level={p.intensity} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ── COUNTDOWN ──────────────────────────────────────────────────── */}
      {view === 'countdown' && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button
                className="workout-control-btn-icon-sm"
                onClick={handleAbort}
                aria-label="Workout abbrechen"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center"></div>
            <div className="workout-control-topbar__slot"></div>
          </div>
          <div className="workout-control-content">
            <div className="hiit-countdown">
              <div className="hiit-countdown-label">Bereit machen</div>
              <div className="hiit-countdown-num">{cdNum}</div>
              <div className="hiit-countdown-cfg">
                {seq.length} Stufen · Peak {seq[peakIdx]}s · {restSecs}s Pause
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── RUNNING / PAUSED ───────────────────────────────────────────── */}
      {(view === 'running' || view === 'paused') && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button
                className="workout-control-btn-icon-sm"
                onClick={handleAbort}
                aria-label="Workout abbrechen"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center">
              <span className={`workout-control-phase-pill workout-control-phase-pill--${
                isWork ? 'work' : 'rest'
              }`}>
                {isWork ? 'ARBEIT' : 'PAUSE'}
              </span>
            </div>
            <div className="workout-control-topbar__slot">
              {/* Music-Slot bleibt leer für Quickstarts (Spec 5.4) */}
            </div>
          </div>

          {/* ── Workout-Content (Timer, Minimap, Progress) ── */}
          <div className="workout-control-content">
            <div className="hiit-timer">
              <div className="hiit-round-info">
                {isWork
                  ? `Stufe ${currentPhase.stepIdx + 1} von ${runSeq.length}${isPeak ? ' · Peak' : ''}`
                  : `Pause · nächste: ${runSeq[currentPhase.stepIdx + 1]}s`
                }
              </div>

              <div className="hiit-timer-display" style={{ color: phaseColor }}>
                <span className="hiit-timer-segment">{timerMins}</span>
                <span className="hiit-timer-colon" style={{ color: phaseColor }}>:</span>
                <span className="hiit-timer-segment">{timerSecs}</span>
              </div>

              <div className="hiit-phase-label" style={{ color: isWork ? 'rgba(239,68,68,0.55)' : 'rgba(96,165,250,0.55)' }}>
                {isWork ? 'Arbeitsphase' : 'Erholungsphase'}
              </div>

              <div className="hiit-progress-wrap">
                <div className="hiit-progress-track">
                  <div className="hiit-progress-fill" ref={progressBarRef} style={{ background: phaseColor }} />
                </div>
                <div className="hiit-progress-labels">
                  <span>{isWork ? 'Arbeitsphase' : 'Erholungsphase'}</span>
                  <span>Stufe {currentPhase.stepIdx + 1}/{runSeq.length}</span>
                </div>
              </div>

              <div className="pyr-minimap">
                <PyramidBars
                  seq={runSeq}
                  peakIdx={runPeakIdx}
                  activeStepIdx={currentPhase.stepIdx}
                  phase={currentPhase.type}
                  maxHeight={42}
                />
              </div>
            </div>
          </div>

          <div className="workout-control-bottombar">
            <button
              className="workout-control-btn-secondary"
              onClick={handleReset}
              aria-label="Reset"
            >
              <i className="bi bi-arrow-counterclockwise" />
            </button>
            <button
              className={`workout-control-btn-primary ${view === 'paused' ? 'is-paused' : 'is-playing'}`}
              onClick={view === 'paused' ? handleResume : handlePause}
              aria-label={view === 'paused' ? 'Weiter' : 'Pause'}
            >
              <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--pause">
                <i className="bi bi-pause-fill" />
              </span>
              <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--play">
                <i className="bi bi-play-fill" />
              </span>
            </button>
            <button
              className="workout-control-btn-secondary"
              onClick={handleSkip}
              aria-label="Überspringen"
            >
              <i className="bi bi-skip-end-fill" />
            </button>
          </div>
        </>
      )}

      {/* ── DONE ───────────────────────────────────────────────────────── */}
      {view === 'done' && (
        <div className="hiit-done">
          <div className="hiit-done-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                 stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
          </div>
          <div className="hiit-done-title">Workout abgeschlossen!</div>
          <div className="hiit-done-sub">Pyramide vollständig!</div>
          <div className="hiit-done-stats">
            {[
              { val: runSeq.length,                             label: 'Stufen' },
              { val: runSeq[runPeakIdx] + 's',                  label: 'Peak' },
              { val: restSecsRef.current + 's',                 label: 'Pause' },
              { val: Math.ceil(calcTotalSeconds(runSeq, restSecsRef.current) / 60) + ' min', label: 'Gesamt' },
            ].map(({ val, label }) => (
              <div key={label} className="hiit-done-stat">
                <div className="hiit-done-stat-val">{val}</div>
                <div className="hiit-done-stat-label">{label}</div>
              </div>
            ))}
          </div>
          <div className="d-flex flex-column gap-3" style={{ maxWidth: 480, margin: '0 auto', padding: '0 16px' }}>
            <button
              className="workout-control-btn-fullwidth"
              onClick={handleReset}
            >
              <i className="bi bi-arrow-clockwise" />
              Erneut starten
            </button>
            <button
              className="workout-control-btn-fullwidth workout-control-btn-fullwidth--ghost"
              onClick={handleAbort}
            >
              Zur Übersicht
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
